import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { currentPageAccess } from "../utils/library";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─────────────────────────────────────────────────────────────────
// Auth helpers
// ─────────────────────────────────────────────────────────────────
const getToken = () => getAuthToken() || "";
const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid() || "",
});

const API_BASE = WEBSERVICE;

// ─────────────────────────────────────────────────────────────────
// Style tokens (same as before)
// ─────────────────────────────────────────────────────────────────
const CARD = {
  background: "var(--body-color-less)",
  border: "1px solid var(--body-color-less2)",
  borderRadius: "12px",
  padding: "20px 22px",
  marginBottom: "16px",
};

const RIGHT_CARD = {
  background: "var(--body-color-less)",
  border: "1px solid var(--body-color-less2)",
  borderRadius: "12px",
  padding: "18px",
};

const FIELD_LABEL = {
  fontSize: "9px",
  fontWeight: "700",
  textTransform: "uppercase",
  letterSpacing: ".4px",
  color: "var(--text-color)",
  opacity: .45,
  marginBottom: "5px",
  display: "block",
};

const INPUT_BASE = {
  width: "100%",
  height: 40,
  padding: "0 14px",
  fontSize: 14,
  borderRadius: 8,
  border: "1.5px solid var(--body-color-less2)",
  background: "var(--body-color)",
  color: "var(--text-color)",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color .15s, box-shadow .15s",
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ─────────────────────────────────────────────────────────────────
// Sub-components (Toast, SectionLabel, FocusInput, RewardTypeCard, StatusBanner, DeleteModal)
// ─────────────────────────────────────────────────────────────────
function Toast({ msg, type }) {
  if (!msg) return null;
  return (
    <div style={{
      position: "fixed", bottom: "24px", right: "24px",
      background: type === "success" ? "#10b981" : "#ef4444",
      color: "#fff", padding: "12px 20px", borderRadius: "12px",
      fontSize: "13px", fontWeight: "600",
      boxShadow: "0 4px 24px rgba(0,0,0,0.25)", zIndex: 9999,
      display: "flex", alignItems: "center", gap: "8px",
      animation: "slideUp .2s ease",
    }}>
      <i className={`mdi ${type === "success" ? "mdi-check-circle" : "mdi-alert-circle"}`} />
      {msg}
    </div>
  );
}

function SectionLabel({ icon, color, text, optional, required }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
      <i className={`mdi ${icon}`} style={{ fontSize: 14, color, opacity: .8 }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-color)", opacity: .7 }}>
        {text}
      </span>
      {required && (
        <span style={{
          fontSize: 10, fontWeight: 500,
          background: "#ef4444",
          color: "#fff",
          borderRadius: 4, padding: "1px 6px",
        }}>
          required
        </span>
      )}
      {optional && (
        <span style={{
          fontSize: 10, fontWeight: 500,
          background: "rgba(100,116,139,.12)",
          color: "var(--text-color)", opacity: .5,
          borderRadius: 4, padding: "1px 6px",
        }}>
          optional
        </span>
      )}
    </div>
  );
}

function FocusInput({ style, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      className={`cc-input${props.className ? " " + props.className : ""}`}
      onFocus={e => { setFocused(true); props.onFocus && props.onFocus(e); }}
      onBlur={e => { setFocused(false); props.onBlur && props.onBlur(e); }}
      style={{
        ...INPUT_BASE,
        ...style,
        borderColor: focused
          ? "var(--btn-color)"
          : "var(--body-color-less2)",
        boxShadow: focused
          ? "0 0 0 3px color-mix(in srgb,var(--btn-color) 14%,transparent)"
          : "none",
      }}
    />
  );
}

function RewardTypeCard({ value, selected, onSelect, icon, color, title, description }) {
  return (
    <div
      onClick={() => onSelect(value)}
      style={{
        flex: "1 1 0",
        padding: "14px 16px",
        borderRadius: 10,
        border: selected
          ? `2px solid ${color}`
          : "1.5px solid var(--body-color-less2)",
        background: selected
          ? `color-mix(in srgb,${color} 8%,transparent)`
          : "var(--body-color)",
        cursor: "pointer",
        transition: "all .15s",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 9, flexShrink: 0,
        background: selected
          ? `color-mix(in srgb,${color} 18%,transparent)`
          : "var(--body-color-less2)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, color: selected ? color : "var(--text-color)",
        transition: "all .15s",
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <div>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: selected ? color : "var(--text-color)",
          marginBottom: 3,
        }}>
          {title}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-color)", opacity: .45, lineHeight: 1.4 }}>
          {description}
        </div>
      </div>
      {selected && (
        <i className="mdi mdi-check-circle" style={{
          fontSize: 16, color, marginLeft: "auto", marginTop: 2,
        }} />
      )}
    </div>
  );
}

const StatusBanner = React.memo(({ status, hasSchedule, isEdit }) => {
  return (
    <div style={{
      ...CARD,
      padding: "16px 20px",
      marginBottom: 20,
      background: status === "scheduled"
        ? "color-mix(in srgb,#f59e0b 6%,transparent)"
        : "color-mix(in srgb,#10b981 6%,transparent)",
      border: `1px solid ${status === "scheduled" ? "#f59e0b" : "#10b981"}`,
      animation: "fadeIn .3s ease",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: status === "scheduled"
            ? "color-mix(in srgb,#f59e0b 15%,transparent)"
            : "color-mix(in srgb,#10b981 15%,transparent)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <i className={`mdi ${status === "scheduled" ? "mdi-calendar-clock" : "mdi-play-circle"}`}
             style={{ fontSize: 24, color: status === "scheduled" ? "#f59e0b" : "#10b981" }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: status === "scheduled" ? "#f59e0b" : "#10b981", marginBottom: 4 }}>
            {isEdit ? "📝 Editing Campaign" : (status === "scheduled" ? "📅 Scheduled Campaign" : "✅ Active Campaign")}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-color)", opacity: 0.7 }}>
            {status === "scheduled"
              ? hasSchedule
                ? "This campaign will run according to the schedule you set below"
                : "This campaign has date restrictions and will run within the specified date range"
              : "This campaign will run immediately once activated (no date or time restrictions)"}
          </div>
        </div>
        <div style={{
          padding: "6px 14px",
          borderRadius: 20,
          background: isEdit ? "var(--btn-color)" : (status === "scheduled" ? "#f59e0b" : "#10b981"),
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
        }}>
          {isEdit ? "EDIT MODE" : (status === "scheduled" ? "SCHEDULED" : "ACTIVE")}
        </div>
      </div>
    </div>
  );
});



// ─────────────────────────────────────────────────────────────────
// Helper: Parse API campaign data to form format
// ─────────────────────────────────────────────────────────────────
const parseCampaignToForm = (campaign) => {
  if (!campaign) return null;
  
  return {
    id: campaign.campaignId || campaign.id,
    scheduleId: campaign.schedules?.scheduleId || null,
    name: campaign.campaignName || campaign.name || "",
    type: campaign.rewardType === "store_credit" ? "store_credit" : "cashback",
    rate: String(campaign.rewardRate || campaign.rate || ""),
    minPurchase: campaign.minPurchase ? String(campaign.minPurchase) : "",
    maxReward: campaign.maxReward ? String(campaign.maxReward) : "",
    startDate: campaign.startDate ? campaign.startDate.split("T")[0] : "",
    endDate: campaign.endDate ? campaign.endDate.split("T")[0] : "",
    noEndDate: !campaign.endDate && campaign.startDate,
    dayOfWeek: campaign.schedules?.dayOfWeek !== undefined && campaign.schedules?.dayOfWeek !== null 
      ? String(campaign.schedules.dayOfWeek) 
      : "",
    startTime: campaign.schedules?.startTime || "",
    endTime: campaign.schedules?.endTime || "",
    status: campaign.status?.toLowerCase() || "active",
  };
};

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
const DEFAULT_FORM = {
  id: null,
  scheduleId: null,
  name: "",
  type: "cashback",
  rate: "",
  minPurchase: "",
  maxReward: "",
  startDate: "",
  endDate: "",
  noEndDate: false,
  dayOfWeek: "",
  startTime: "",
  endTime: "",
  status: "active",
};

export default function CampaignForm() {
  const history = useHistory();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [toast, setToast] = useState({ msg: "", type: "success" });
  const [timeError, setTimeError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch campaign data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchCampaign();
    }
  }, [id]);

  const fetchCampaign = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/campaigns/merchant?merchantId=${getMerchantId()}&status=all`,
        { headers: authHeaders() }
      ).then(r => r.json());
      
      if (res.status && Array.isArray(res.data)) {
        const campaign = res.data.find(c => c.campaignId === id || c.id === id);
        if (campaign) {
          setForm(parseCampaignToForm(campaign));
        } else {
          showMsg("error", "Campaign not found");
          setTimeout(() => history.push("/rewards/campaigns"), 1500);
        }
      }
    } catch {
      showMsg("error", "Failed to load campaign data");
    } finally {
      setFetching(false);
    }
  };

  const showMsg = useCallback((type, msg) => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), 3500);
  }, []);

  const updateFormField = useCallback((key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
  }, []);

  const handleFieldChange = useCallback((key) => (e) => {
    const value = e.target.value;
    updateFormField(key, value);

    if (key === "startTime") {
      if (value && !form.endTime) {
        setTimeError("End time is required when start time is set");
      } else {
        setTimeError("");
      }
    } else if (key === "endTime") {
      if (!value && form.startTime) {
        setTimeError("End time is required when start time is set");
      } else {
        setTimeError("");
      }
    }
  }, [updateFormField, form.startTime, form.endTime]);

  const handleCheckboxChange = useCallback((e) => {
    const checked = e.target.checked;
    setForm(prev => ({
      ...prev,
      noEndDate: checked,
      endDate: checked ? "" : prev.endDate,
    }));
  }, []);

  const handleSelectChange = useCallback((key) => (e) => {
    updateFormField(key, e.target.value);
  }, [updateFormField]);

  const getAutoStatus = useCallback(() => {
    const hasDaySchedule = form.dayOfWeek !== "";
    const hasTimeSchedule = (form.startTime && form.startTime.trim() !== "") ||
                           (form.endTime && form.endTime.trim() !== "");

    if (hasDaySchedule || hasTimeSchedule) {
      return "scheduled";
    }
    return "active";
  }, [form.dayOfWeek, form.startTime, form.endTime]);

  const hasValidSchedule = useCallback(() => {
    return (form.dayOfWeek !== "") ||
           (form.startTime && form.startTime.trim() !== "") ||
           (form.endTime && form.endTime.trim() !== "");
  }, [form.dayOfWeek, form.startTime, form.endTime]);

  const currentStatus = getAutoStatus();

  const isTimeValid = () => {
    if (form.startTime && !form.endTime) return false;
    if (form.endTime && !form.startTime) return false;
    return true;
  };

  const validateTimeWithError = useCallback(() => {
    if (form.startTime && !form.endTime) {
      setTimeError("End time is required when start time is set");
      return false;
    }
    if (form.endTime && !form.startTime) {
      setTimeError("Start time is required when end time is set");
      return false;
    }
    setTimeError("");
    return true;
  }, [form.startTime, form.endTime]);

  const canSave =
    form.name.trim() &&
    form.rate &&
    parseFloat(form.rate) > 0 &&
    parseFloat(form.rate) <= 100 &&
    form.startDate && form.startDate.trim() !== "" &&
    isTimeValid();

  const handleSave = useCallback(async () => {
    if (currentPageAccess() === "READ") {
      showMsg("error", "You need write access to perform this operation");
      return;
    }
    if (!canSave) return;
    if (!validateTimeWithError()) return;

    setLoading(true);
    try {
      const schedules = {};
      let hasAnySchedule = false;

      // Include scheduleId if it exists
      if (form.scheduleId !== null && form.scheduleId !== undefined) {
        schedules.scheduleId = form.scheduleId;
        hasAnySchedule = true;
      }

      if (form.dayOfWeek !== "") {
        schedules.dayOfWeek = Number(form.dayOfWeek);
        hasAnySchedule = true;
      }

      if (form.startTime && form.startTime.trim() !== "") {
        schedules.startTime = form.startTime;
        hasAnySchedule = true;
      }

      if (form.endTime && form.endTime.trim() !== "") {
        schedules.endTime = form.endTime;
        hasAnySchedule = true;
      }

      const payload = {
        merchantId: getMerchantId(),
        campaignName: form.name.trim(),
        rewardType: form.type,
        rewardRate: Number(form.rate),
        minPurchase: form.minPurchase ? Number(form.minPurchase) : 0,
        maxReward: form.maxReward ? Number(form.maxReward) : 0,
        startDate: form.startDate || null,
        endDate: (() => {
          if (form.noEndDate) {
            const base = form.startDate ? new Date(form.startDate) : new Date();
            base.setFullYear(base.getFullYear() + 1);
            return base.toISOString().split("T")[0];
          }
          return form.endDate || null;
        })(),
        status: currentStatus,
        schedules: hasAnySchedule ? schedules : null,
      };

      // Add campaignId if in edit mode
      if (isEditMode && form.id) {
        payload.campaignId = form.id;
      }

      const action = isEditMode ? "UPDATE" : "INSERT";
      const url = `${API_BASE}/campaigns/createreward?action=${action}`;

      const res = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      }).then(r => { 
        if (!r.ok) throw new Error("Request failed"); 
        return r.json(); 
      });

      if (res.status) {
        showMsg("success", isEditMode 
          ? `Campaign "${form.name}" updated!` 
          : `Campaign "${form.name}" created!`);
        setTimeout(() => history.push("/rewards/campaigns"), 1200);
      } else {
        showMsg("error", res.message || `Failed to ${isEditMode ? 'update' : 'create'} campaign`);
      }
    } catch {
      showMsg("error", `Failed to ${isEditMode ? 'update' : 'create'} campaign. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, [canSave, validateTimeWithError, form, currentStatus, showMsg, history, isEditMode]);

  const handleDeleteClick = useCallback(() => {
    if (currentPageAccess() === "READ") {
      showMsg("error", "You need write access to perform this operation");
      return;
    }
    if (!isEditMode || !form.id) return;
    setShowDeleteModal(true);
  }, [form.id, isEditMode, showMsg]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !form.id) return;

    setDeleting(true);
    try {
      const payload = {
        merchantId: getMerchantId(),
        campaignId: form.id,
      };

      // Include scheduleId if it exists
      if (form.scheduleId !== null && form.scheduleId !== undefined) {
        payload.scheduleId = form.scheduleId;
      }

      const url = `${API_BASE}/campaigns/createreward?action=DELETE`;

      const res = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      }).then(r => { 
        if (!r.ok) throw new Error("Request failed"); 
        return r.json(); 
      });

      if (res.status) {
        showMsg("success", `Campaign "${form.name}" deleted!`);
        setShowDeleteModal(false);
        setTimeout(() => history.push("/rewards/campaigns"), 1200);
      } else {
        showMsg("error", res.message || "Failed to delete campaign");
        setShowDeleteModal(false);
      }
    } catch {
      showMsg("error", "Failed to delete campaign. Please try again.");
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  }, [form.id, form.name, form.scheduleId, showMsg, history, isEditMode]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  // Preview summary
  const scheduleLabel = (() => {
    const day = form.dayOfWeek !== "" ? DAY_NAMES[Number(form.dayOfWeek)] : null;
    const time = form.startTime && form.endTime ? `${form.startTime}–${form.endTime}` : null;
    const dateRange = form.startDate ? `${form.startDate}${form.noEndDate ? " onwards" : form.endDate ? ` → ${form.endDate}` : ""}` : null;

    const parts = [];
    if (day) parts.push(day);
    if (time) parts.push(time);
    if (dateRange && !day && !time) parts.push(dateRange);

    return parts.length > 0 ? parts.join(" • ") : null;
  })();

  const previewItems = [
    { label: "Campaign Name", value: form.name.trim() || null, color: "var(--btn-color)", required: true },
    { label: "Reward Type",
      value: form.type ? (form.type === "cashback" ? "Cashback" : "Store Credit") : null,
      color: form.type === "cashback" ? "#818cf8" : "#2dd4bf", required: true },
    { label: "Reward Rate", value: form.rate ? `${form.rate}%` : null, color: "#f59e0b", required: true },
    { label: "Start Date", value: form.startDate || null, color: "#8b5cf6", required: true },
    { label: "End Date", value: form.noEndDate ? "No end date" : (form.endDate || null), color: "#8b5cf6", required: false },
    { label: "Min Purchase", value: form.minPurchase ? `$${form.minPurchase}` : null, color: "#10b981", required: false },
    { label: "Max Reward", value: form.maxReward ? `$${form.maxReward}` : null, color: "#ec4899", required: false },
    { label: "Schedule", value: scheduleLabel, color: "#8b5cf6", required: false },
  ];

  const missingRequiredFields = [];
  if (!form.name.trim()) missingRequiredFields.push("Campaign Name");
  if (!form.rate || parseFloat(form.rate) <= 0 || parseFloat(form.rate) > 100) missingRequiredFields.push("Reward Rate (1-100%)");
  if (!form.type) missingRequiredFields.push("Reward Type");
  if (!form.startDate) missingRequiredFields.push("Start Date");

  if (fetching) {
    return (
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={{ padding: "60px 24px", textAlign: "center" }}>
            <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 32, color: "var(--btn-color)" }} />
            <p style={{ marginTop: 16, color: "var(--text-color)", opacity: 0.5 }}>Loading campaign...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <style>{`
        @keyframes slideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .cc-grid>*{min-width:0;}
        .cc-form-grid-2col{display:grid;grid-template-columns:1fr 1fr;gap:12px;width:100%;align-items:start;}
        .cc-form-grid-3col{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;width:100%;align-items:start;}
        .cc-form-field{display:flex;flex-direction:column;min-height:62px;}
        .cc-form-field label{min-height:14px;line-height:1.2;}
        .cc-form-field input,.cc-form-field select{width:100%;box-sizing:border-box;}
        @media(min-width:992px) and (max-width:1142px){
          .cc-form-field label{font-size:7px!important;letter-spacing:.2px!important;margin-bottom:3px!important;}
        }
        @media(max-width:1150px){
          .cc-form-grid-3col{grid-template-columns:1fr!important;}
        }
        @media(max-width:900px){
          .cc-grid{grid-template-columns:1fr!important;}
          .cc-form-grid-2col{grid-template-columns:1fr!important;}
          .cc-form-grid-3col{grid-template-columns:1fr!important;}
        }
        .cc-input::placeholder{color:var(--text-color);opacity:0.35;}
      `}</style>

      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={{ padding: "32px 24px 100px" }}>
            <Toast msg={toast.msg} type={toast.type} />
            

            {/* Header */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <button
                  onClick={() => history.push("/rewards/campaigns")}
                  style={{
                    width: 30, height: 30, borderRadius: 8,
                    border: "1px solid var(--body-color-less2)",
                    background: "transparent", color: "var(--text-color)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", fontSize: 16, opacity: .6,
                    transition: "opacity .15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                  onMouseLeave={e => e.currentTarget.style.opacity = ".6"}
                >
                  <i className="mdi mdi-arrow-left" />
                </button>
                <h2 style={{ fontSize: "22px", fontWeight: "700", color: "var(--text-color)",
                  margin: 0, letterSpacing: "-.3px" }}>
                  {isEditMode ? "Edit Reward Campaign" : "Create Reward Campaign"}
                </h2>
              </div>
              <p style={{ fontSize: 13, color: "var(--text-color)", opacity: .45,
                margin: "2px 0 0 40px" }}>
                {isEditMode 
                  ? "Update your campaign settings" 
                  : "Set up a campaign to automatically reward customers after payments"}
              </p>
            </div>

            {/* Status Banner */}
            <StatusBanner status={currentStatus} hasSchedule={hasValidSchedule()} isEdit={isEditMode} />

            {/* Required Information Card */}
            <div style={{
              ...CARD,
              background: missingRequiredFields.length > 0
                ? "color-mix(in srgb,#ef4444 4%,transparent)"
                : "color-mix(in srgb,#10b981 4%,transparent)",
              border: missingRequiredFields.length > 0
                ? "1px solid color-mix(in srgb,#ef4444 30%,transparent)"
                : "1px solid color-mix(in srgb,#10b981 30%,transparent)",
            }}>
              <SectionLabel
                icon="mdi-alert-circle-outline"
                color={missingRequiredFields.length > 0 ? "#ef4444" : "#10b981"}
                text="Required Information"
              />

              {missingRequiredFields.length > 0 ? (
                <>
                  <div style={{
                    fontSize: 13,
                    color: "#ef4444",
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}>
                    <i className="mdi mdi-alert" />
                    Please fill in the following required fields:
                  </div>
                  <ul style={{
                    margin: 0,
                    paddingLeft: 20,
                    color: "var(--text-color)",
                    opacity: 0.7,
                    fontSize: 12,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px 16px",
                    listStyle: "none"
                  }}>
                    {missingRequiredFields.map((field, idx) => (
                      <li key={idx} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <i className="mdi mdi-close-circle" style={{ color: "#ef4444", fontSize: 12 }} />
                        {field}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div style={{
                  fontSize: 13,
                  color: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}>
                  <i className="mdi mdi-check-circle" />
                  All required fields are filled! Ready to {isEditMode ? 'update' : 'create'} campaign.
                </div>
              )}
            </div>

            {/* Grid */}
            <div className="cc-grid" style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,60fr) minmax(0,40fr)",
              gap: 20, alignItems: "start",
            }}>
              {/* LEFT - Form Fields */}
              <div>
                {/* Campaign Name */}
                <div style={CARD}>
                  <SectionLabel icon="mdi-text-box-outline" color="#6366f1" text="Campaign Name" required />
                  <FocusInput
                    type="text"
                    placeholder="e.g. Weekend Cashback"
                    value={form.name}
                    onChange={handleFieldChange("name")}
                  />
                </div>

                {/* Reward Type */}
                <div style={CARD}>
                  <SectionLabel icon="mdi-gift-outline" color="var(--btn-color)" text="Reward Type" required />
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <RewardTypeCard
                      value="cashback"
                      selected={form.type === "cashback"}
                      onSelect={(v) => updateFormField("type", v)}
                      icon="mdi-cash-refund"
                      color="#818cf8"
                      title="Cashback"
                      description="Real money credited to customer's cashback balance. Withdrawable."
                    />
                    <RewardTypeCard
                      value="store_credit"
                      selected={form.type === "store_credit"}
                      onSelect={(v) => updateFormField("type", v)}
                      icon="mdi-ticket-percent"
                      color="#2dd4bf"
                      title="Store Credit"
                      description="Merchant-specific credit. Redeemable at your checkout only."
                    />
                  </div>
                </div>

                {/* Reward Rate & Limits */}
                <div style={CARD}>
                  <SectionLabel icon="mdi-percent" color="#ec4899" text="Reward Rate & Limits" required />

                  <div style={{ marginBottom: 16 }}>
                    <label style={FIELD_LABEL}>Reward Rate (%) *</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ position: "relative", flex: "0 0 140px" }}>
                        <FocusInput
                          type="number"
                          placeholder="5"
                          min="0.1"
                          max="100"
                          value={form.rate}
                          onChange={handleFieldChange("rate")}
                          style={{ paddingRight: 36 }}
                        />
                        <span style={{
                          position: "absolute", right: 12, top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 14, fontWeight: 700,
                          color: "var(--text-color)", opacity: .4,
                        }}>%</span>
                      </div>
                      {form.rate && (
                        <div style={{
                          padding: "6px 12px", borderRadius: 8,
                          background: "color-mix(in srgb,var(--btn-color) 10%,transparent)",
                          border: "1px solid color-mix(in srgb,var(--btn-color) 20%,transparent)",
                          fontSize: 13, color: "var(--btn-color)", fontWeight: 600,
                          animation: "fadeIn .2s ease",
                        }}>
                          Customer earns {form.rate}¢ per $1 spent
                        </div>
                      )}
                    </div>
                    {form.rate && (parseFloat(form.rate) > 50) && (
                      <div style={{ marginTop: 6, fontSize: 11, color: "#f59e0b",
                        display: "flex", alignItems: "center", gap: 5 }}>
                        <i className="mdi mdi-alert-outline" style={{ fontSize: 12 }} />
                        High reward rate — make sure this is intentional
                      </div>
                    )}
                  </div>

                  <div className="cc-form-grid-2col">
                    <div className="cc-form-field">
                      <label style={FIELD_LABEL}>Min Purchase <span style={{ opacity: .5 }}>(optional)</span></label>
                      <div style={{ position: "relative", width: "100%", boxSizing: "border-box" }}>
                        <span style={{
                          position: "absolute", left: 12, top: 12,
                          fontSize: 14, color: "var(--text-color)", opacity: .35,
                          pointerEvents: "none",
                        }}>$</span>
                        <FocusInput
                          type="number"
                          placeholder="0.00"
                          min="0"
                          value={form.minPurchase}
                          onChange={handleFieldChange("minPurchase")}
                          style={{ paddingLeft: 26, width: "100%", boxSizing: "border-box" }}
                        />
                      </div>
                    </div>
                    <div className="cc-form-field">
                      <label style={FIELD_LABEL}>Max Reward Per Txn <span style={{ opacity: .5 }}>(optional)</span></label>
                      <div style={{ position: "relative", width: "100%", boxSizing: "border-box" }}>
                        <span style={{
                          position: "absolute", left: 12, top: 12,
                          fontSize: 14, color: "var(--text-color)", opacity: .35,
                          pointerEvents: "none",
                        }}>$</span>
                        <FocusInput
                          type="number"
                          placeholder="0.00"
                          min="0"
                          value={form.maxReward}
                          onChange={handleFieldChange("maxReward")}
                          style={{ paddingLeft: 26, width: "100%", boxSizing: "border-box" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Range */}
                <div style={CARD}>
                  <SectionLabel icon="mdi-calendar-range" color="#8b5cf6" text="Campaign Date Range" required />

                  <div className="cc-form-grid-2col" style={{ marginBottom: 12 }}>
                    <div className="cc-form-field">
                      <label style={FIELD_LABEL}>Start Date *</label>
                      <FocusInput
                        type="date"
                        value={form.startDate}
                        onChange={handleFieldChange("startDate")}
                        required
                      />
                    </div>
                    <div className="cc-form-field">
                      <label style={FIELD_LABEL}>End Date</label>
                      <FocusInput
                        type="date"
                        value={form.endDate}
                        onChange={handleFieldChange("endDate")}
                        disabled={form.noEndDate}
                        style={{ opacity: form.noEndDate ? .4 : 1 }}
                      />
                    </div>
                  </div>

                  <label style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    cursor: "pointer", fontSize: 13, color: "var(--text-color)", opacity: .65,
                  }}>
                    <input
                      type="checkbox"
                      checked={form.noEndDate}
                      onChange={handleCheckboxChange}
                      style={{ accentColor: "var(--btn-color)", width: 14, height: 14 }}
                    />
                    No end date — run indefinitely
                  </label>
                </div>

                {/* Day & Time Schedule */}
                <div style={CARD}>
                  <SectionLabel icon="mdi-clock-outline" color="#a855f7" text="Day & Time Schedule" optional />

                  <div style={{
                    marginTop: 0, padding: "14px", borderRadius: 10,
                    border: "1px dashed var(--body-color-less2)",
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: ".6px", color: "var(--text-color)", opacity: .4, marginBottom: 12 }}>
                      Time Window <span style={{ fontWeight: 400 }}>(optional - leave blank for any time)</span>
                    </div>
                    <div className="cc-form-grid-3col">
                      <div className="cc-form-field">
                        <label style={FIELD_LABEL}>Day of Week</label>
                        <select
                          value={form.dayOfWeek}
                          onChange={handleSelectChange("dayOfWeek")}
                          style={{ ...INPUT_BASE, height: 40, padding: "0 12px" }}
                        >
                          <option value="7">Any day</option>
                          {DAY_NAMES.map((d, i) => (
                            <option key={i} value={i}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="cc-form-field">
                        <label style={FIELD_LABEL}>Start Time</label>
                        <FocusInput
                          type="time"
                          value={form.startTime}
                          onChange={handleFieldChange("startTime")}
                        />
                      </div>
                      <div className="cc-form-field">
                        <label style={FIELD_LABEL}>End Time</label>
                        <FocusInput
                          type="time"
                          value={form.endTime}
                          onChange={handleFieldChange("endTime")}
                        />
                      </div>
                    </div>
                    {timeError && (
                      <div style={{
                        marginTop: 8,
                        fontSize: 11,
                        color: "#ef4444",
                        display: "flex",
                        alignItems: "center",
                        gap: 5
                      }}>
                        <i className="mdi mdi-alert-circle" style={{ fontSize: 12 }} />
                        {timeError}
                      </div>
                    )}
                    <div style={{
                      marginTop: 10,
                      fontSize: 11,
                      color: "var(--text-color)",
                      opacity: 0.45,
                      display: "flex",
                      alignItems: "center",
                      gap: 5
                    }}>
                      <i className="mdi mdi-information-outline" style={{ fontSize: 12 }} />
                      {form.dayOfWeek || form.startTime || form.endTime
                        ? "Schedule is set — campaign will follow these time restrictions"
                        : "No schedule set — campaign will run any day, any time within the date range"}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT - sticky preview */}
              <div style={{ position: "sticky", top: 24, display: "flex",
                flexDirection: "column", gap: 16 }}>

                <div style={RIGHT_CARD}>
                  <div style={{
                    fontSize: "10px", fontWeight: "700", textTransform: "uppercase",
                    letterSpacing: ".7px", color: "var(--text-color)", opacity: .4,
                    marginBottom: 14,
                  }}>
                    Campaign Preview
                  </div>

                  {previewItems.map(({ label, value, color, required }) => (
                    <div key={label} style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "6px 0",
                    }}>
                      <i className={value ? "mdi mdi-check-circle" : required ? "mdi mdi-alert-circle" : "mdi mdi-circle-outline"}
                        style={{
                          fontSize: 15, flexShrink: 0,
                          color: value ? "#10b981" : required ? "#ef4444" : "var(--text-color)",
                          opacity: value ? 1 : required ? 0.8 : 0.2,
                        }}
                      />
                      <span style={{ fontSize: 13, color: "var(--text-color)", opacity: value ? 1 : required ? 0.8 : 0.35, flex: 1 }}>
                        {label}
                        {required && !value && <span style={{ fontSize: 10, marginLeft: 6, color: "#ef4444" }}>(required)</span>}
                      </span>
                      {value && (
                        <span style={{
                          fontSize: 11, fontWeight: 600, color,
                          background: `color-mix(in srgb,${color} 12%,transparent)`,
                          borderRadius: 5, padding: "2px 7px",
                          maxWidth: 110, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {value}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {canSave && (
                  <div style={{
                    ...RIGHT_CARD,
                    background: "color-mix(in srgb,#10b981 6%,transparent)",
                    border: "1px solid color-mix(in srgb,#10b981 20%,transparent)",
                    animation: "fadeIn .3s ease",
                  }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <i className="mdi mdi-check-circle" style={{ fontSize: 16, color: "#10b981", marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-color)", marginBottom: 5 }}>
                          Ready to {isEditMode ? 'update' : 'create'}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-color)", opacity: .55, lineHeight: 1.6 }}>
                          Customers will earn{" "}
                          <strong style={{ color: "var(--text-color)", opacity: 1 }}>
                            {form.rate}%{" "}
                            {form.type === "cashback" ? "cashback" : "store credit"}
                          </strong>
                          {form.minPurchase ? ` on purchases above $${form.minPurchase}` : " on every payment"}
                          {form.maxReward ? `, capped at $${form.maxReward}` : ""}.
                          <br />
                          <strong style={{ color: currentStatus === "scheduled" ? "#f59e0b" : "#10b981" }}>
                            Status: {currentStatus === "scheduled" ? "Scheduled" : "Active"}
                          </strong>
                          {currentStatus === "scheduled" && " (based on your date/schedule settings)"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!canSave && (
                  <div style={{
                    ...RIGHT_CARD,
                    textAlign: "center",
                    padding: "24px 18px",
                    background: "transparent",
                    border: "1px dashed var(--body-color-less2)",
                  }}>
                    <i className="mdi mdi-gift-outline" style={{
                      fontSize: 28, color: "var(--text-color)", opacity: .2,
                      display: "block", marginBottom: 10,
                    }} />
                    <div style={{ fontSize: 12, color: "var(--text-color)", opacity: .35, lineHeight: 1.6 }}>
                      Fill in all required fields (Name, Reward Rate, and Start Date) to get started
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* Sticky CTA */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "var(--body-color-less)",
        borderTop: "1px solid var(--body-color-less2)",
        padding: "12px 24px", zIndex: 9,
        backdropFilter: "blur(8px)",
      }}>
        <div style={{
          maxWidth: 960, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10,
        }}>
          <button
            onClick={() => history.push("/rewards/campaigns")}
            style={{
              padding: "10px 22px", borderRadius: 10,
              border: "1.5px solid var(--body-color-less2)",
              background: "transparent", color: "var(--text-color)",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              opacity: .7, transition: "opacity .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "1"}
            onMouseLeave={e => e.currentTarget.style.opacity = ".7"}
          >
            Cancel
          </button>
          <button
            disabled={!canSave || loading}
            onClick={handleSave}
            style={{
              padding: "10px 28px", borderRadius: 10, border: "none",
              background: canSave ? "var(--btn-color)" : "var(--body-color-less2)",
              color: canSave ? "#fff" : "var(--text-color)",
              opacity: canSave ? (loading ? .7 : 1) : .5,
              fontSize: 13, fontWeight: 600,
              cursor: canSave && !loading ? "pointer" : "not-allowed",
              display: "inline-flex", alignItems: "center", gap: 6,
              transition: "all .2s",
              boxShadow: canSave ? "0 0 14px color-mix(in srgb,var(--btn-color) 30%,transparent)" : "none",
            }}
          >
            {loading ? (
              <><i className="mdi mdi-loading mdi-spin" /> {isEditMode ? 'Updating…' : 'Creating…'}</>
            ) : (
              <><i className="mdi mdi-check" /> {isEditMode ? 'Update Campaign' : 'Create Campaign'}</>
            )}
          </button>
        </div>
      </div>
    </Fragment>
  );
}